import type { FormEvent } from 'react'

import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import Button from '@/components/ui/button'
import Field from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { OptionSelect } from '@/components/generals/form-selects'
import { SectionHeading } from '@/components/generals/view-parts'
import { CURRENT_USER } from '@/constants/companies'
import { SUPPORT_TOPICS } from '@/constants/crm'
import { apiRequest } from '@/lib/client-api'

const FAQS = [
    {
        question: "How do I add a company, deal or contact?",
        answer: "Use the New button on each page, or press Ctrl or Cmd and K to open search and pick an action. Everything you add is saved for the whole team right away."
    },
    {
        question: "How do I move a deal to the next stage?",
        answer: "On the Deals Board, drag the card into the next column. You can also open the three dot menu on a card and pick a stage, which works well on phones and tablets."
    },
    {
        question: "How is the forecast calculated?",
        answer: "Deals closing in the selected quarter are grouped by stage. Closed won counts in full, negotiation is commit, proposal is best case, and earlier stages are pipeline. The weighted forecast multiplies each deal value by the likelihood of its stage."
    },
    {
        question: "What counts as a slipping deal?",
        answer: "Any open deal whose close date has already passed. From the Slipping Deals report you can push the date out by 30 days, mark the deal as lost, or open it to edit."
    },
    {
        question: "Do email sequences send emails?",
        answer: "Not yet. Sequences plan the steps your team works through and keep track of them. Ask us if you would like them connected to an email service."
    },
    {
        question: "How do I invite my teammates?",
        answer: "Choose Invite teammates at the bottom of the sidebar. You can copy the CRM link or write an invite in your own email app."
    }
]

const BILLING_TOPIC = "Billing and plans"

interface HelpViewProps {
    initialTopic?: string
}

export default function HelpView({ initialTopic }: HelpViewProps) {
    const [name, setName] = useState(CURRENT_USER.name)
    const [email, setEmail] = useState(CURRENT_USER.email)
    const [topic, setTopic] = useState(initialTopic === 'billing' ? BILLING_TOPIC : SUPPORT_TOPICS[0])
    const [message, setMessage] = useState('')
    const [website, setWebsite] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [sent, setSent] = useState(false)
    const [submitting, setSubmitting] = useState(false)

    async function handleSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault()

        if (submitting) return

        if (message.trim().length < 10) {
            setError("Tell us a little more, at least a sentence.")

            return
        }

        setSubmitting(true)
        setError(null)

        const result = await apiRequest<{ ok: true }>('/api/support', 'POST', { name, email, topic, message, website })

        setSubmitting(false)

        if (!result.ok) {
            setError(result.message)

            return
        }

        setSent(true)
        setMessage('')
    }

    return (
        <div className='flex min-h-0 flex-1 overflow-y-auto'>
            <div className='mx-auto grid w-full max-w-[1100px] grid-cols-1 gap-8 p-4 lg:grid-cols-[minmax(0,1fr)_400px] lg:p-8'>
                <section className='flex flex-col gap-4'>
                    <SectionHeading title="Common questions" />
                    <div className='border-border divide-border flex flex-col divide-y rounded-xl border'>
                        {FAQS.map(item => (
                            <details key={item.question} className='group'>
                                <summary className='hover:bg-white/[0.02] flex cursor-pointer list-none items-center justify-between gap-4 px-4 py-4 transition-colors duration-150 [&::-webkit-details-marker]:hidden'>
                                    <span className='lead-style font-medium leading-[1.35]'>{item.question}</span>
                                    <ChevronDown aria-hidden className='text-soft size-4 shrink-0 transition-transform duration-200 group-open:rotate-180' />
                                </summary>
                                <p className='text-soft max-w-[48em] px-4 pb-4 leading-[1.5]'>
                                    {item.answer}
                                </p>
                            </details>
                        ))}
                    </div>
                </section>

                <section id='contact-support' className='flex flex-col gap-4'>
                    <SectionHeading title="Contact support" />
                    <div className='border-border rounded-xl border p-5'>
                        {sent ? (
                            <div role='status' className='flex flex-col items-start gap-3'>
                                <span className='lead-style font-medium'>Thanks, your message is in.</span>
                                <p className='text-soft leading-[1.5]'>
                                    We reply to {email} as soon as we can, usually within one working day.
                                </p>
                                <Button variant='secondary' size='sm' onClick={() => setSent(false)}>
                                    Send another message
                                </Button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} noValidate className='flex flex-col gap-4' aria-busy={submitting}>
                                <Field label="Your name" htmlFor='help-name' required>
                                    <Input id='help-name' value={name} onChange={event => setName(event.target.value)} maxLength={120} autoComplete='name' required />
                                </Field>
                                <Field label="Email for our reply" htmlFor='help-email' required>
                                    <Input id='help-email' type='email' value={email} onChange={event => setEmail(event.target.value)} maxLength={200} autoComplete='email' required />
                                </Field>
                                <Field label="Topic" htmlFor='help-topic'>
                                    <OptionSelect id='help-topic' value={topic} onChange={setTopic} options={SUPPORT_TOPICS} />
                                </Field>
                                <Field label="How can we help?" htmlFor='help-message' required error={error ?? undefined}>
                                    <textarea
                                        id='help-message'
                                        value={message}
                                        onChange={event => {
                                            setMessage(event.target.value)
                                            if (error) setError(null)
                                        }}
                                        rows={5}
                                        maxLength={4000}
                                        placeholder="Describe what you are trying to do and what happened"
                                        aria-invalid={error ? true : undefined}
                                        aria-describedby={error ? 'help-message-error' : undefined}
                                        className='border-line-strong bg-secondary text-foreground placeholder:text-subtle focus-visible:border-ring aria-invalid:border-danger min-h-[120px] w-full resize-y rounded-lg border px-3 py-2.5 text-[14px] leading-[1.45] outline-none transition-[border-color] duration-150'
                                    />
                                </Field>
                                <div aria-hidden className='absolute -left-[9999px] h-px w-px overflow-hidden'>
                                    <label htmlFor='help-website'>Website</label>
                                    <input id='help-website' tabIndex={-1} autoComplete='off' value={website} onChange={event => setWebsite(event.target.value)} />
                                </div>
                                <Button variant='primary' size='sm' type='submit' disabled={submitting} className='self-start'>
                                    {submitting ? "Sending…" : "Send message"}
                                </Button>
                            </form>
                        )}
                    </div>
                </section>
            </div>
        </div>
    )
}
